<script setup lang="ts">
import { loadScript } from '@mop/shared/utils/util';

defineOptions({
  name: 'CompanySocialFeed',
});

type CompanySocialFeedData = {
  enabled?: boolean;
  appId?: string;
  title?: string;
  description?: string;
};

const props = defineProps({
  data: {
    type: Object as PropType<CompanySocialFeedData>,
    required: true,
  },
});

const enabled = props.data?.enabled ?? true;
const DEFAULT_APP_ID = 'pid-d7a5f040-9193-4ae0-af01-a30f1c06e7cb';
const SOCIAL_FEED_SCRIPT_SOURCE = 'https://cdn.commoninja.com/sdk/latest/commonninja.js';
const socialFeedContainerRef = ref<HTMLDivElement | null>(null);
const appId = props.data?.appId || DEFAULT_APP_ID;

let videoObserver: MutationObserver | null = null;

function setupVideoForAutoplay(video: HTMLVideoElement) {
  video.autoplay = true;
  video.muted = true;
  video.defaultMuted = true;
  video.loop = true;
  video.playsInline = true;

  video.play();
}

function handleCommonNinjaVideos() {
  const videos = socialFeedContainerRef.value?.getElementsByTagName('video');

  if (!videos || videos.length === 0) {
    return false;
  }

  setTimeout(() => {
    for (let i = 0; i < videos.length; i++) {
      const video = videos[i];
      if (video) {
        setupVideoForAutoplay(video);
      }
    }
  }, 500);

  return true;
}

function startCommonNinjaAutoplayHandling() {
  if (!socialFeedContainerRef.value) {
    return;
  }

  videoObserver = new MutationObserver(() => {
    const isHandled = handleCommonNinjaVideos();
    if (isHandled && videoObserver) {
      videoObserver.disconnect();
      videoObserver = null;
    }
  });

  videoObserver.observe(socialFeedContainerRef.value, {
    childList: true,
    subtree: true,
  });
}

onMounted(() => {
  if (!enabled) {
    return;
  }

  if (window.CommonNinja) {
    window.CommonNinja.reload();
    startCommonNinjaAutoplayHandling();
    return;
  }

  loadScript({
    source: SOCIAL_FEED_SCRIPT_SOURCE,
    triggerCallbackIfExists: true,
    callback: () => {
      startCommonNinjaAutoplayHandling();
    },
  });
});

onUnmounted(() => {
  if (videoObserver) {
    videoObserver.disconnect();
    videoObserver = null;
  }
});
</script>

<template>
  <div v-if="enabled" v-storyblok-editable="data" class="company-social-feed">
    <div v-if="data.title" class="company-social-feed__headline">
      <Ui2Heading :level="2" is-uppercase>
        {{ data.title }}
      </Ui2Heading>
      <div v-if="data.description" class="company-social-feed__description">
        {{ data.description }}
      </div>
    </div>
    <div class="company-social-feed__content">
      <div ref="socialFeedContainerRef" class="company-social-feed__feed-wrapper">
        <div :class="`commonninja_component ${appId}`"></div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.company-social-feed {
  display: flex;
  flex-direction: column;
  padding: 0 $ui-global-padding;
  margin-bottom: 60px;
  gap: $space-16;
}

.company-social-feed__headline {
  display: flex;
  flex-direction: column;
  gap: $space-4;
}
.company-social-feed__content {
  display: flex;
  justify-content: center;
  min-height: 120px;
}

.company-social-feed__feed-wrapper {
  width: 90%;

  :deep() {
    .feed-header,
    .slider-container .slick-arrow {
      @include z(global, content);
    }
  }
}
</style>
